// CNPJ utilities supporting both the legacy numeric format and the alphanumeric format
// introduced by Receita Federal (Instrução Normativa RFB nº 2.229/2024, in force from July 2026).
//
// Rules from the official spec:
// - CNPJ has exactly 14 characters.
// - Positions 1-12 may be digits (0-9) or uppercase letters (A-Z).
// - Positions 13-14 (check digits) are always numeric.
// - Modulo-11 weights stay the same; each character contributes (charCode - 48).
//   For digits this yields the digit value (0-9); for uppercase letters A-Z it yields 17-42.
// - Input must be normalized to uppercase before any operation.

const CNPJ_LENGTH = 14
const CNPJ_BASE_LENGTH = 12

const FIRST_DV_WEIGHTS = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]
const SECOND_DV_WEIGHTS = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]

const ALPHANUMERIC_CHAR = /[0-9A-Z]/
const NON_ALPHANUMERIC = /[^0-9A-Za-z]/g
const HAS_LETTER = /[A-Z]/

const REPEATED_NUMERIC_CNPJS = new Set([
  '00000000000000',
  '11111111111111',
  '22222222222222',
  '33333333333333',
  '44444444444444',
  '55555555555555',
  '66666666666666',
  '77777777777777',
  '88888888888888',
  '99999999999999'
])

export const CNPJ_MASK = 'NN.NNN.NNN/NNNN-##'

// Custom token for vue-the-mask: alphanumeric with forced uppercase.
// IMPORTANT: vue-the-mask ships a pre-bundled UMD build (`dist/vue-the-mask.js`)
// with the `tokens` object inlined. The directive at runtime uses that bundled
// singleton — NOT `src/tokens.js`. We therefore mutate the named export from the
// package entry, which exposes the very same in-memory object.
// Mutation is idempotent.
export const registerCnpjMaskToken = () => {
  // eslint-disable-next-line global-require
  const { tokens } = require('vue-the-mask')
  if (!tokens.N) {
    tokens.N = {
      pattern: /[0-9a-zA-Z]/,
      transform: v => v.toLocaleUpperCase()
    }
  }
}

const charValue = char => char.charCodeAt(0) - 48

const computeCheckDigit = (chars, weights) => {
  let sum = 0
  for (let i = 0; i < weights.length; i++) {
    sum += charValue(chars[i]) * weights[i]
  }
  const remainder = sum % 11
  return remainder < 2 ? 0 : 11 - remainder
}

export const sanitizeCnpj = value => {
  if (value === null || value === undefined) return ''
  return String(value).replace(NON_ALPHANUMERIC, '').toLocaleUpperCase().slice(0, CNPJ_LENGTH)
}

export const isAlphanumericCnpj = value => HAS_LETTER.test(sanitizeCnpj(value))

export const formatCnpj = value => {
  const raw = sanitizeCnpj(value)
  if (!raw) return ''
  let output = ''
  let cursor = 0
  for (const maskChar of CNPJ_MASK) {
    if (cursor >= raw.length) break
    if (maskChar === 'N' || maskChar === '#') {
      output += raw[cursor]
      cursor++
    } else {
      output += maskChar
    }
  }
  return output
}

export const validCnpj = value => {
  if (value === null || value === undefined || value === '') return true
  const cnpj = sanitizeCnpj(value)
  if (cnpj.length !== CNPJ_LENGTH) return false
  if (REPEATED_NUMERIC_CNPJS.has(cnpj)) return false

  for (let i = 0; i < CNPJ_LENGTH; i++) {
    if (!ALPHANUMERIC_CHAR.test(cnpj[i])) return false
  }

  // Last two positions are always numeric.
  if (!/^\d$/.test(cnpj[12]) || !/^\d$/.test(cnpj[13])) return false

  const base = cnpj.slice(0, CNPJ_BASE_LENGTH)
  const firstDv = computeCheckDigit(base, FIRST_DV_WEIGHTS)
  if (firstDv !== Number(cnpj[12])) return false

  const baseWithFirstDv = cnpj.slice(0, CNPJ_BASE_LENGTH + 1)
  const secondDv = computeCheckDigit(baseWithFirstDv, SECOND_DV_WEIGHTS)
  if (secondDv !== Number(cnpj[13])) return false

  return true
}
